const GRAPH_BASE_URL = 'https://graph.microsoft.com/v1.0';

interface GraphUserResponse {
  displayName?: string;
}

function readBlobAsDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.addEventListener('load', () => {
      if (typeof reader.result === 'string') {
        resolve(reader.result);
      } else {
        reject(new Error('Profile photo did not produce a data URL'));
      }
    });
    reader.addEventListener('error', () =>
      reject(reader.error ?? new Error('Failed to read profile photo')),
    );
    reader.readAsDataURL(blob);
  });
}

/**
 * Gets a profile photo directly from Microsoft Graph using the SPA token.
 * Omitting userId fetches the current user's photo.
 */
export async function getGraphProfilePhoto(
  getGraphToken: () => Promise<string>,
  userId?: string,
): Promise<string | undefined> {
  const target = userId ? `users/${encodeURIComponent(userId)}` : 'me';
  const url = `${GRAPH_BASE_URL}/${target}/photo/$value`;

  console.info(
    `[profile-photo][spa] Acquiring Microsoft Graph profile token for ${target}`,
  );
  const token = await getGraphToken();
  console.info(
    `[profile-photo][spa] Graph token acquired; requesting /${target}/photo`,
  );

  const response = await fetch(url, {
    headers: { Authorization: `Bearer ${token}` },
  });
  console.info(
    `[profile-photo][spa] Graph returned ${response.status} ${response.headers.get('Content-Type') ?? 'unknown content type'} for ${target}`,
  );

  if (response.status === 404) {
    console.info(`[profile-photo][spa] ${target} has no Graph photo`);
    return undefined;
  }
  if (!response.ok) {
    throw new Error(
      `Microsoft Graph profile photo failed for ${target}: ${response.status}`,
    );
  }

  const blob = await response.blob();
  console.info(
    `[profile-photo][spa] Received ${blob.size} profile-photo bytes for ${target}`,
  );
  return readBlobAsDataUrl(blob);
}

/**
 * Gets a user's display name directly from Microsoft Graph.
 */
export async function getGraphUserDisplayName(
  getGraphToken: () => Promise<string>,
  userId: string,
): Promise<string | undefined> {
  const encodedUserId = encodeURIComponent(userId);
  const url = `${GRAPH_BASE_URL}/users/${encodedUserId}?$select=displayName`;
  const token = await getGraphToken();
  const response = await fetch(url, {
    headers: { Authorization: `Bearer ${token}` },
  });

  console.info(
    `[profile-name][spa] Graph returned ${response.status} for users/${encodedUserId}`,
  );

  if (response.status === 404) return undefined;
  if (!response.ok) {
    throw new Error(
      `Microsoft Graph user lookup failed for ${userId}: ${response.status}`,
    );
  }

  const user = (await response.json()) as GraphUserResponse;
  const displayName = user.displayName?.trim();
  return displayName === '' ? undefined : displayName;
}
